import {useCallback, useEffect, useRef, type PointerEvent as ReactPointerEvent} from 'react';
import {ChevronDown, X} from 'lucide-react';
import {APP_BY_ID} from '../data/apps';
import {useDesktop} from '../desktop/DesktopContext';
import type {WindowState} from '../types';
import {forgetParkedShot, useSheetMotion} from './sheetMotion';

/** Drag distance that commits the dismiss. */
const DISMISS_DISTANCE = 110;
/** …or a fast flick, in px/ms, so a short decisive swipe also works. */
const DISMISS_VELOCITY = 0.55;

interface DragState {
  dy: number;
  lastTime: number;
  lastY: number;
  pointerId: number;
  startY: number;
  velocity: number;
}

/**
 * A running app, full-screen.
 *
 * On the desktop an app is a window the user positions; on a phone the only
 * honest layout is full-bleed, so the window chrome collapses into one header:
 * park (keeps the app running, returns home) and close. Dragging that header
 * down parks the app too — the gesture is an accelerator for the button, never
 * the only way to do it.
 *
 * Opening and putting away are flights between the app icon and the full
 * screen, run by `sheetMotion.ts` — the same two machines as the desktop
 * (transform flight and genie warp), aimed at an icon instead of a parking
 * slot. An app that vanishes instantly leaves the user hunting for it; an app
 * that folds into the icon it came out of has already said where it went.
 */
export function AppSheet({state}: {state: WindowState}) {
  const {closeWindow, goHome} = useDesktop();
  const app = APP_BY_ID[state.appId];

  const sheetRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const frameRef = useRef<number | null>(null);
  const pendingRef = useRef<number | null>(null);
  const leavingRef = useRef(false);
  const {dismiss: fly, parked, prewarm} = useSheetMotion(state.appId, sheetRef);

  // Same discipline as the dock: pointer moves are coalesced into one frame and
  // the only thing written is a transform.
  const schedule = useCallback((dy: number) => {
    pendingRef.current = dy;
    if (frameRef.current !== null) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      const next = pendingRef.current;
      pendingRef.current = null;
      const el = sheetRef.current;
      if (el && next !== null) el.style.transform = `translate3d(0, ${next}px, 0)`;
    });
  }, []);

  useEffect(
    () => () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    },
    [],
  );

  const dismiss = useCallback(
    (action: () => void, keep = true) => {
      if (leavingRef.current) return;
      leavingRef.current = true;
      fly(action, {keep});
    },
    [fly],
  );

  const park = useCallback(() => dismiss(goHome), [dismiss, goHome]);

  const close = useCallback(() => {
    // A closed app has no bitmap waiting for it: next time it opens fresh, out
    // of its icon, rather than unfolding a picture of a session that is over.
    forgetParkedShot(state.appId);
    dismiss(() => closeWindow(state.id), false);
  }, [closeWindow, dismiss, state.appId, state.id]);

  // The browser/hardware back button parks the app too, but that entry is owned
  // by the shell (`useSheetHistory`) — see the note there for why it cannot live
  // in this component.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') goHome();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [goHome]);

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (leavingRef.current) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if ((event.target as HTMLElement).closest('button')) return;

    dragRef.current = {
      dy: 0,
      lastTime: event.timeStamp,
      lastY: event.clientY,
      pointerId: event.pointerId,
      startY: event.clientY,
      velocity: 0,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    // A drag on the header usually ends in a dismiss, and it lasts long enough
    // to pay for the bitmap several times over.
    prewarm();
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.pointerId) return;

    // Downward only: an upward drag on the header should do nothing rather than
    // lift the sheet off the top of the screen.
    const dy = Math.max(0, event.clientY - drag.startY);
    const dt = event.timeStamp - drag.lastTime;
    if (dt > 0) drag.velocity = (event.clientY - drag.lastY) / dt;
    drag.dy = dy;
    drag.lastTime = event.timeStamp;
    drag.lastY = event.clientY;
    schedule(dy);
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.pointerId) return;
    dragRef.current = null;

    const committed =
      drag.dy > DISMISS_DISTANCE || (drag.velocity > DISMISS_VELOCITY && drag.dy > 24);
    if (committed) {
      park();
      return;
    }

    const el = sheetRef.current;
    if (!el) return;
    el.style.transition = 'transform 220ms cubic-bezier(0.32, 0.72, 0, 1)';
    el.style.transform = '';
    window.setTimeout(() => {
      if (sheetRef.current) sheetRef.current.style.transition = '';
    }, 240);
  };

  if (!app) return null;
  const View = app.view;

  return (
    <div
      ref={sheetRef}
      className="mobile-sheet fixed inset-0 z-[9500] flex flex-col bg-canvas"
      // While the genie overlay is up it is holding an exact copy of this
      // sheet, so the real one must stop painting — not stop existing, or the
      // app would re-lay-out on the way out.
      data-parked={parked ? '' : undefined}
      role="dialog"
      aria-label={app.name}
    >
      <div
        className="mobile-sheet__grabber bar safe-top shrink-0"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div className="mx-auto mt-2 h-1 w-9 rounded-full bg-ink-3/40" />
        <div className="flex h-12 items-center gap-2 px-2">
          <button
            type="button"
            aria-label="Về màn hình chính"
            onClick={park}
            // A finger landing here is the warning the desktop gets from a
            // hovering pointer: rasterise now, while it is still free.
            onPointerDown={prewarm}
            className="tap flex items-center justify-center rounded-control text-ink-2 active:text-ink"
          >
            <ChevronDown size={22} />
          </button>
          <p className="min-w-0 flex-1 truncate text-center text-[15px] font-bold text-ink">
            {app.name}
          </p>
          <button
            type="button"
            aria-label={`Đóng ${app.name}`}
            onClick={close}
            onPointerDown={prewarm}
            className="tap flex items-center justify-center rounded-control text-ink-2 active:text-ink"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      <div className="safe-bottom min-h-0 flex-1 overflow-hidden">
        <View />
      </div>
    </div>
  );
}
